import { isMobileBreakpoint, mediaQueryLarge, onDocumentReady } from "@theme/utilities";
import { ensureGsap, prefersReducedMotion as prefersReducedMotion$1 } from "@theme/vp-motion";
//#region assets/vp-footer-accordion.ts
/**
* Smooth GSAP open/close for footer menu accordions on mobile.
*
* Replaces the CSS `block-size: 0 → auto` transition on accordion-custom,
* which reads as jagged on mobile. GSAP writes inline height/opacity and
* overrides that CSS via the `vp-accordion-gsap` class.
*/
var DURATION_OPEN = .32;
var DURATION_CLOSE = .28;
var EASE_OPEN = "power2.out";
var EASE_CLOSE = "power2.inOut";
/** @type {typeof window.gsap | null} */
var gsap = null;
/** @param {number} value */
var reduced = (value) => prefersReducedMotion$1() ? 0 : value;
/**
* @param {HTMLElement} content
* @returns {number}
*/
function contentHeight(content) {
	return content.scrollHeight;
}
var FooterAccordion = class {
	/** @type {HTMLElement} */
	#root;
	/** @type {HTMLDetailsElement} */
	#details;
	/** @type {HTMLElement} */
	#summary;
	/** @type {HTMLElement} */
	#content;
	/** @type {{ kill: () => void } | null} */
	#tween = null;
	/** @type {AbortController} */
	#controller = new AbortController();
	/**
	* @param {HTMLElement} root
	* @param {HTMLDetailsElement} details
	* @param {HTMLElement} summary
	* @param {HTMLElement} content
	*/
	constructor(root, details, summary, content) {
		this.#root = root;
		this.#details = details;
		this.#summary = summary;
		this.#content = content;
		this.#root.classList.add("vp-accordion-gsap");
		this.#syncIdleState();
		const { signal } = this.#controller;
		this.#summary.addEventListener("click", this.#onClick, { signal });
		mediaQueryLarge.addEventListener("change", this.#onBreakpoint, { signal });
	}
	disconnect() {
		this.#tween?.kill();
		this.#tween = null;
		this.#controller.abort();
		this.#root.classList.remove("vp-accordion-gsap");
		gsap?.set(this.#content, { clearProps: "height,opacity,overflow" });
	}
	#syncIdleState = () => {
		if (!gsap) return;
		if (!isMobileBreakpoint() || this.#details.open) {
			gsap.set(this.#content, { clearProps: "height,opacity,overflow" });
			return;
		}
		gsap.set(this.#content, {
			height: 0,
			opacity: 0,
			overflow: "hidden"
		});
	};
	#onBreakpoint = () => {
		this.#tween?.kill();
		this.#tween = null;
		this.#syncIdleState();
	};
	/**
	* @param {MouseEvent} event
	*/
	#onClick = (event) => {
		if (!isMobileBreakpoint()) return;
		if (this.#root.dataset.disableOnMobile === "true") return;
		if (!gsap) return;
		event.preventDefault();
		if (this.#details.open) this.#close();
		else this.#open();
	};
	#open() {
		if (!gsap) return;
		this.#tween?.kill();
		const wasOpen = this.#details.open;
		this.#details.open = true;
		const startHeight = wasOpen ? this.#content.offsetHeight : 0;
		const startOpacity = wasOpen ? Number(gsap.getProperty(this.#content, "opacity")) : 0;
		gsap.set(this.#content, {
			height: startHeight,
			opacity: startOpacity,
			overflow: "hidden"
		});
		const endHeight = contentHeight(this.#content);
		this.#tween = gsap.to(this.#content, {
			height: endHeight,
			opacity: 1,
			duration: reduced(DURATION_OPEN),
			ease: EASE_OPEN,
			overwrite: true,
			onComplete: () => {
				gsap?.set(this.#content, {
					height: "auto",
					overflow: "hidden"
				});
				this.#tween = null;
			}
		});
	}
	#close() {
		if (!gsap) return;
		this.#tween?.kill();
		const startHeight = this.#content.offsetHeight;
		gsap.set(this.#content, {
			height: startHeight,
			overflow: "hidden"
		});
		this.#tween = gsap.to(this.#content, {
			height: 0,
			opacity: 0,
			duration: reduced(DURATION_CLOSE),
			ease: EASE_CLOSE,
			overwrite: true,
			onComplete: () => {
				this.#details.open = false;
				this.#tween = null;
			}
		});
	}
};
/**
* @returns {void}
*/
function enhanceFooterAccordions() {
	const roots = document.querySelectorAll(".footer-content accordion-custom.menu--accordion");
	for (const root of roots) {
		if (!(root instanceof HTMLElement)) continue;
		if (root.classList.contains("vp-accordion-gsap")) continue;
		const details = root.querySelector(":scope > .menu__details") ?? root.querySelector(":scope > details");
		const summary = details?.querySelector(":scope > summary");
		const content = details?.querySelector(":scope > .details-content");
		if (!(details instanceof HTMLDetailsElement)) continue;
		if (!(summary instanceof HTMLElement)) continue;
		if (!(content instanceof HTMLElement)) continue;
		new FooterAccordion(root, details, summary, content);
	}
}
onDocumentReady(async () => {
	gsap = await ensureGsap();
	if (!gsap) return;
	enhanceFooterAccordions();
});
//#endregion
